/**
 * Currency Formatting Utility
 * 
 * Formats currency amounts based on user preferences
 * Falls back to AUD if preferences not available
 */

import type { UserPreferences } from '../types/preference.types';

/**
 * Currency code to locale mapping
 */
const CURRENCY_LOCALES: Record<string, string> = {
  USD: 'en-US',
  EUR: 'de-DE', // Using German locale for EUR (common in EU)
  GBP: 'en-GB',
  JPY: 'ja-JP',
  CAD: 'en-CA',
  AUD: 'en-AU',
};

/**
 * Currency code to symbol mapping
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: '$',
  AUD: '$',
};

/**
 * Format currency amount based on user preferences
 */
export function formatCurrencyWithPreferences(
  amount: number,
  preferences?: UserPreferences | null,
  options?: {
    showSymbol?: boolean;
    showDecimals?: boolean;
    absolute?: boolean;
    originalCurrency?: string; // For displaying original currency on existing transactions
  }
): string {
  const { 
    showSymbol: _showSymbol = true, 
    showDecimals = true, 
    absolute = false,
    originalCurrency 
  } = options || {};

  // Use original currency if provided (for existing transactions)
  const currency = originalCurrency || preferences?.currency || 'AUD';
  const currencyDisplay = preferences?.currencyDisplay || 'symbol';
  
  const value = absolute ? Math.abs(amount) : amount;
  const locale = CURRENCY_LOCALES[currency] || 'en-AU';
  
  // Format number
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(value);

  // If currencyDisplay is 'code', replace symbol with code
  if (currencyDisplay === 'code') {
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return formatted.replace(symbol, `${currency} `).trim();
  }

  return formatted;
}

/**
 * Format transaction amount with type indicator
 */
export function formatTransactionAmount(
  amount: number,
  type: 'debit' | 'credit' | 'transfer',
  preferences?: UserPreferences | null,
  originalCurrency?: string
): string {
  const formatted = formatCurrencyWithPreferences(
    Math.abs(amount),
    preferences,
    {
      showSymbol: true,
      showDecimals: true,
      absolute: true,
      originalCurrency,
    }
  );

  if (type === 'credit') {
    return `+${formatted}`;
  } else if (type === 'transfer') {
    return `→${formatted}`;
  } else {
    return `-${formatted}`;
  }
}

